import { createContext, useContext } from "react";

import {
  createAbilityFromRules,
  type AppAbility as PermissionAppAbility,
  type RbacConfig as RbacConfigType,
} from "@pbz/permission/index";

// Re-export types for convenience
export type AppAbility = PermissionAppAbility;
export type RbacConfig = RbacConfigType;

import { createContextualCan } from "@casl/react";

// Context for ability
export const AbilityContext = createContext<AppAbility | null>(null);

// Context for rbacConfig
export const RbacConfigContext = createContext<RbacConfig | null>(null);

// Create the Can component with our ability context
export const Can = createContextualCan(
  AbilityContext.Consumer as Parameters<typeof createContextualCan>[0],
);

// Hook to use the ability context
export function useAbility(): AppAbility {
  const ability = useContext(AbilityContext);
  if (!ability) {
    throw new Error("useAbility must be used within an AbilityProvider");
  }
  return ability;
}

// Hook to get rbacConfig from the API response
export function useRbacConfig(): RbacConfig {
  const rbacConfig = useContext(RbacConfigContext);
  if (!rbacConfig) {
    throw new Error("useRbacConfig must be used within an AbilityProvider");
  }
  return rbacConfig;
}

// Hook to check permissions
export function usePermissions() {
  const ability = useAbility();

  return {
    can: ability.can.bind(ability),
    cannot: ability.cannot.bind(ability),
    ability,
  };
}

// Function to create ability from API response
export function buildAbilityFromApiResponse(
  _apiRbacConfig: RbacConfig,
  abilities: Array<{
    action: string | string[];
    subject: string | string[];
    conditions?: Record<string, unknown>;
  }>,
): AppAbility {
  // Use the permission package to create ability from rules
  return createAbilityFromRules(abilities || []);
}
